const GENERIC_PARTICIPANT_LABELS = new Set([
  "candidate",
  "participant",
  "guest",
  "user",
  "speaker",
  "interviewee",
  "interviewer",
  "host",
  "admin",
  "co-host",
  "co host",
]);

export const isGenericParticipantLabel = (name) => {
  const clean = String(name || "").trim().toLowerCase();
  return !clean || GENERIC_PARTICIPANT_LABELS.has(clean);
};

export const isEmailLike = (value) => {
  const clean = String(value || "").trim();
  if (!clean.includes("@")) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean);
};

export const derivePersonNameFromEmail = (email, fallback = "Interviewer") => {
  const clean = String(email || "").trim();
  if (!clean) return fallback;
  const localPart = clean.split("@")[0].replace(/\d+$/, "").replace(/[._-]+/g, " ").trim();
  if (!localPart) return fallback;
  const words = localPart.split(/\s+/).filter(Boolean);
  if (!words.length) return fallback;
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const resolveRoleFallbackName = (participant, metadata = {}) => {
  const role = String(metadata.role || "").trim().toLowerCase();
  const identity = String(participant?.identity || "").trim().toLowerCase();
  if (role === "host" || identity.startsWith("host-")) return "Host";
  if (role === "candidate" || identity.startsWith("candidate-")) return "Candidate";
  if (role === "interviewer" || identity.startsWith("interviewer-")) return "Interviewer";
  return "Participant";
};

export const normalizeGuestDisplayName = (value) =>
  String(value || "").trim().replace(/\s+/g, " ");

/** True when a guest-entered name is usable in the meeting UI (instant open links). */
export const isValidGuestDisplayName = (value) => {
  const clean = normalizeGuestDisplayName(value);
  return clean.length >= 2 && !isGenericParticipantLabel(clean);
};

export const toFirstName = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "";
  const [first] = clean.split(/\s+/);
  if (!first) return "";
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

export const dedupeTitleSegments = (title) => {
  const raw = String(title || "").trim();
  if (!raw) return "";

  const parts = raw
    .split(/\s*[-–—]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);

  const seen = new Set();
  const unique = [];
  parts.forEach((part) => {
    const key = part.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(part);
  });

  return unique.length ? unique.join(" — ") : raw;
};

export const formatLiveInterviewDisplayTitle = ({
  title = "",
  jobTitle = "",
  candidateName = "",
} = {}) => {
  const dedupedTitle = dedupeTitleSegments(title);
  if (dedupedTitle) return dedupedTitle;

  const cleanJobTitle = String(jobTitle || "").trim();
  const cleanCandidateName = String(candidateName || "").trim();

  if (cleanJobTitle && cleanCandidateName) {
    if (cleanCandidateName.toLowerCase() === cleanJobTitle.toLowerCase()) {
      return cleanCandidateName;
    }
    if (cleanJobTitle.toLowerCase().includes(cleanCandidateName.toLowerCase())) {
      return dedupeTitleSegments(cleanJobTitle);
    }
    return `${cleanJobTitle} — ${cleanCandidateName}`;
  }

  return cleanJobTitle || cleanCandidateName || "Live Interview";
};

const looksLikePersonName = (value) => {
  const clean = String(value || "").trim();
  if (!clean || isGenericParticipantLabel(clean)) return false;
  if (clean.includes("@")) return false;
  if (/^\d+$/.test(clean)) return false;
  const words = clean.split(/\s+/).filter(Boolean);
  return words.length >= 1 && words.length <= 4 && words.every((word) => /^[a-zA-Z][a-zA-Z.'-]*$/.test(word));
};

const PARTICIPANT_AVATAR_COLORS = [
  "#796e8c",
  "#5c6bc0",
  "#00897b",
  "#8d6e63",
  "#7b1fa2",
  "#546e7a",
  "#c2185b",
  "#6d4c41",
  "#455a64",
  "#5e35b1",
];

const parseParticipantMetadata = (participant) => {
  try {
    return JSON.parse(participant?.metadata || "{}");
  } catch {
    return {};
  }
};

export const resolveParticipantDisplayName = (participant) => {
  const metadata = parseParticipantMetadata(participant);
  const metaName = normalizeGuestDisplayName(
    metadata.display_name || metadata.displayName || metadata.name || ""
  );
  if (isValidGuestDisplayName(metaName) && !isEmailLike(metaName)) {
    return metaName;
  }

  const displayName = String(participant?.name || "").trim();
  if (displayName && !isGenericParticipantLabel(displayName) && !isEmailLike(displayName)) {
    return displayName;
  }

  const metadataEmail = String(metadata.email || "").trim();
  if (isEmailLike(metadataEmail)) {
    return derivePersonNameFromEmail(
      metadataEmail,
      resolveRoleFallbackName(participant, metadata),
    );
  }

  if (isEmailLike(displayName)) {
    return derivePersonNameFromEmail(
      displayName,
      resolveRoleFallbackName(participant, metadata),
    );
  }

  const identity = String(participant?.identity || "").trim().toLowerCase();
  if (identity.startsWith("candidate-")) return "Candidate";

  const emailLike = String(participant?.identity || "").split("@")[0] || "";
  const fromIdentity = emailLike.replace(/[._-]+/g, " ").trim();
  if (looksLikePersonName(fromIdentity)) {
    return fromIdentity
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  if (identity.startsWith("host-")) return "Host";
  if (identity.startsWith("interviewer-")) return "Interviewer";

  return displayName && !isEmailLike(displayName)
    ? displayName
    : resolveRoleFallbackName(participant, metadata);
};

const LOBBY_IDENTITY_PREFIXES = ["host-", "interviewer-", "candidate-", "admin-"];

const decodeLobbyIdentityEmail = (identity) => {
  let slug = String(identity || "").trim().toLowerCase();
  if (!slug) return "";

  for (const prefix of LOBBY_IDENTITY_PREFIXES) {
    if (slug.startsWith(prefix)) {
      slug = slug.slice(prefix.length);
      break;
    }
  }

  if (!slug) return "";
  if (isEmailLike(slug)) return slug;

  const segments = slug.split("-").filter(Boolean);
  if (segments.length >= 3) {
    const tld = segments[segments.length - 1];
    const domain = segments[segments.length - 2];
    const local = segments.slice(0, -2).join("-");
    if (/^[a-z]+$/.test(tld) && /^[a-z0-9]+$/.test(domain) && local) {
      return `${local}@${domain}.${tld}`;
    }
  }

  return "";
};

const lobbyRoleFallback = (role) => {
  if (role === "host") return "Host";
  if (role === "interviewer") return "Interviewer";
  if (role === "candidate") return "Candidate";
  return "Participant";
};

export const resolveLobbyPresenceDisplayName = (
  person,
  { candidateName = "" } = {},
) => {
  const raw = String(person?.display_name || "").trim();
  const role = String(person?.role || "").trim().toLowerCase();
  const identity = String(person?.identity || "").trim();

  if (raw && !isGenericParticipantLabel(raw) && !isEmailLike(raw)) {
    return raw;
  }

  if (role === "candidate") {
    const resolvedCandidate = String(candidateName || "").trim();
    if (
      resolvedCandidate &&
      !isGenericParticipantLabel(resolvedCandidate) &&
      !isEmailLike(resolvedCandidate)
    ) {
      return resolvedCandidate;
    }
    return "Candidate";
  }

  const emailCandidate =
    (isEmailLike(raw) ? raw : "") ||
    (isEmailLike(identity) ? identity : "") ||
    decodeLobbyIdentityEmail(identity);

  if (isEmailLike(emailCandidate)) {
    return derivePersonNameFromEmail(emailCandidate, lobbyRoleFallback(role));
  }

  if (role === "host" || role === "interviewer") {
    return lobbyRoleFallback(role);
  }

  return raw || lobbyRoleFallback(role);
};

export const getParticipantInitials = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "U";

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
  }

  const single = words[0] || clean;
  if (single.length >= 2) return single.slice(0, 2).toUpperCase();
  return single.charAt(0).toUpperCase();
};

export const getParticipantAvatarColor = (name) => {
  const clean = String(name || "").trim().toLowerCase();
  if (!clean) return PARTICIPANT_AVATAR_COLORS[0];

  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) {
    hash = clean.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PARTICIPANT_AVATAR_COLORS[Math.abs(hash) % PARTICIPANT_AVATAR_COLORS.length];
};

export const resolveInviteCandidateDisplay = ({
  candidateName = "",
  candidateEmail = "",
  roomTitle = "",
  jobTitle = "",
} = {}) => {
  const rawName = String(candidateName || "").trim();
  const fullTitle = formatLiveInterviewDisplayTitle({
    title: roomTitle,
    jobTitle,
    candidateName: isGenericParticipantLabel(rawName) ? "" : rawName,
  });
  const segments = fullTitle
    ? fullTitle.split(/\s*—\s*/).map((part) => part.trim()).filter(Boolean)
    : [];

  let resolvedName = isGenericParticipantLabel(rawName) ? "" : rawName;
  if (!resolvedName && segments.length >= 2) {
    const trailingName = segments[segments.length - 1];
    if (looksLikePersonName(trailingName)) {
      resolvedName = trailingName;
    }
  }

  if (!resolvedName && candidateEmail) {
    const localPart = String(candidateEmail).split("@")[0] || "";
    const fromEmail = localPart.replace(/[._-]+/g, " ").trim();
    if (looksLikePersonName(fromEmail)) {
      resolvedName = fromEmail
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
  }

  let jobHeading = fullTitle || "Live Interview";
  if (resolvedName && segments.length >= 2 && segments[segments.length - 1] === resolvedName) {
    jobHeading = segments.slice(0, -1).join(" — ") || fullTitle;
  } else if (resolvedName && jobTitle) {
    const cleanJob = dedupeTitleSegments(jobTitle);
    if (cleanJob && !cleanJob.toLowerCase().includes(resolvedName.toLowerCase())) {
      jobHeading = cleanJob;
    }
  }

  return {
    firstName: toFirstName(resolvedName),
    fullName: resolvedName,
    jobHeading: jobHeading || "Live Interview",
  };
};

/** True when room allows open/direct guest join (instant Live Meet). */
export const isOpenJoinLiveMeetRoom = (roomData) => {
  const room = roomData?.room || {};
  const schedulingMode = String(room.scheduling_mode || "").trim().toLowerCase();
  if (["instant", "instant_live_meet"].includes(schedulingMode)) {
    return true;
  }
  if (room.open_join === true) {
    return true;
  }
  const invite = roomData?.candidate_invite || roomData?.invite;
  if (String(invite?.join_mode || "").trim().toLowerCase() === "direct") {
    return true;
  }
  const invites = roomData?.candidate_invites || [];
  return invites.some(
    (row) => String(row?.join_mode || "").trim().toLowerCase() === "direct"
  );
};
